import React, { FunctionComponent, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import { IconContext } from "react-icons/lib"
import { FaCheck } from "react-icons/fa6"
import { FiEdit } from "react-icons/fi"
import { ColumnHeaderCell, ColumnRowCell } from "@murphy-frontend/web-core/components/Table/Models"
import { IoChevronBackOutline } from "react-icons/io5"
import Table from "@murphy-frontend/web-core/components/Table"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import { useUser } from "../../../common/contexts/UserContext"
import Button from "@murphy-frontend/web-core/components/Button"
import TabPanel, { TabType } from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import ComponentWrapper from "../../../common/components/ComponentWrapper"
import EditUserForm from "../components/EditUserForm"
import { useGetUser } from "@murphy-frontend/common/api/Users/Queries"
import { useParams } from "react-router-dom"
import { UserEducationColumns } from "@murphy-frontend/web-core/columns"
import { convertLevels, convertTrainingStates } from "@murphy-frontend/common/utils"
import FilterTag from "@murphy-frontend/web-core/components/FilterTag"

const EditUser: FunctionComponent = () => {
	const { id } = useParams()
	const navigate = useNavigate()

	const [selectedTabId, setSelectedTabId] = useState<number>(0)
	const [educationRows, setEducationRows] = useState<ColumnRowCell[][]>([])
	const [educationColumns, setEducationColumns] = useState<ColumnHeaderCell[]>([])
	const [disabled, setIsDisabled] = useState(true)

	const { customer, allCustomerProducts } = useCustomer()
	const { user } = useUser()
	const { translations } = useUserPreferences()

	const { isLoading: isLoadingUser, isError: isErrorUser, data: dataUser, error: errorUser } = useGetUser(id)

	const onClickTab = (tabId: number) => {
		setSelectedTabId(tabId)
	}

	const levelTag = (level: number) => {
		const field = convertLevels(level, translations)

		return <FilterTag filterName={field} level={level} />
	}

	useEffect(() => {
		if (translations) {
			setEducationColumns(UserEducationColumns(translations))
		}
	}, [translations])

	useEffect(() => {
		if (isLoadingUser) return

		if (dataUser.UserTrainingSessions) {
			const education = dataUser.UserTrainingSessions.filter(_ => ![2, 4].includes(_.TrainingSessionStateID)).map(
				(training, idx) => {
					return [
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: training.TrainingName,
							value: training.TrainingName,
							columnKey: "name"
						},
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: training.Levels ? levelTag(training.Levels) : "",
							// translated for sorting
							value: training.Levels ? convertLevels(training.Levels, translations) : "Z",
							customClass: "text-center",
							columnKey: "level"
						},
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: training.IsMandatory ? (
								<IconContext.Provider value={{ className: "btn-icon" }}>
									<FaCheck />
								</IconContext.Provider>
							) : (
								""
							),
							value: training.IsMandatory,
							columnKey: "mandatory"
						},
						{
							id: training.TrainingId,
							rowId: idx,
							displayValue: convertTrainingStates(training.TrainingSessionStateID, translations),
							value: convertTrainingStates(training.TrainingSessionStateID, translations),
							columnKey: "status"
						}
					]
				}
			)

			setEducationRows(education)
		}
	}, [dataUser, isLoadingUser])

	const initialTabs: TabType[] = [
		{
			id: 0,
			displayText: "Profile",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		},
		{
			id: 1,
			displayText: "Education",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		}
	]

	const handleClick = () => {
		setIsDisabled(!disabled)
	}

	if (!allCustomerProducts || !user || !customer || isLoadingUser) {
		return (
			<ComponentWrapper active={8} subMenuActive={2}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	return (
		<ComponentWrapper active={8} subMenuActive={2}>
			<section className='header-btns'>
				<div className='header-back-btn'>
					<Button buttonSize={"btn--medium"} isLightButton={true} onClick={() => navigate("/users")}>
						<IconContext.Provider value={{ className: "btn-icon" }}>
							<IoChevronBackOutline />
						</IconContext.Provider>
						Back
					</Button>
				</div>
			</section>
			<section className='header-btns'>
				<div className='header-back-btn'>
					<h2>{dataUser.Username}</h2>
				</div>
				<div className='header-right-btn'>
					{selectedTabId === 0 && (
						<Button buttonSize={"btn--medium"} isLightButton={disabled} onClick={handleClick}>
							<IconContext.Provider value={{ className: "btn-icon useradmin-more" }}>
								<FiEdit />
								Edit User Profile
							</IconContext.Provider>
						</Button>
					)}
				</div>
			</section>
			<section className='table-container-general'>
				<div className='user-tab-wrapper'>
					<TabPanel
						tabs={initialTabs}
						onClickTab={onClickTab}
						selectedTabId={selectedTabId}
						customClass='tab-solid-panel-tms'
					/>
				</div>
				{selectedTabId === 1 && <Table rows={educationRows} columns={educationColumns} key={"education"} />}
			</section>
			{selectedTabId === 0 && (
				<section className='card-container edit-card-container'>
					<EditUserForm
						translations={translations}
						userData={dataUser}
						products={allCustomerProducts}
						disabled={disabled}
					/>
				</section>
			)}
		</ComponentWrapper>
	)
}

export default EditUser
